import React, { useMemo } from 'react'
import clsx from 'clsx'
import {
  ChatBubbleOvalLeftIcon,
  FaceSmileIcon,
  FireIcon,
} from '@heroicons/react/24/outline'
import { Text } from '../../ui'
import { SocialPost } from '../../../types/social'
import AuthorAvatar from './AuthorAvatar'
import { mentionPreviewText } from './mentionUtils'
import { DashboardCardSkeleton } from '../DashboardSkeleton'
import {
  feedCardClass,
  getPostInteractionCount,
  getRankBadgeClass,
} from './socialUtils'

const TOP_POSTS_LIMIT = 5

export interface TopPostsPanelProps {
  posts: SocialPost[]
  loading: boolean
  onSelectPost: (postId: string) => void
}

const rankBadgeClass = getRankBadgeClass

const TopPostsPanel: React.FC<TopPostsPanelProps> = ({ posts, loading, onSelectPost }) => {
  const topPosts = useMemo(
    () =>
      [...posts]
        .filter((post) => getPostInteractionCount(post) > 0)
        .sort((a, b) => getPostInteractionCount(b) - getPostInteractionCount(a))
        .slice(0, TOP_POSTS_LIMIT),
    [posts]
  )

  return (
    <aside className={feedCardClass}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <FireIcon className="h-5 w-5 text-primary-500 flex-shrink-0" />
          <div>
            <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
              Top postagens
            </Text>
            <Text size="xs" color="muted">
              Mais reações e comentários
            </Text>
          </div>
        </div>
      </div>

      <div className="p-2">
        {loading && (
          <div className="space-y-2 p-2">
            {Array.from({ length: 3 }, (_, i) => (
              <DashboardCardSkeleton key={i} className="h-16" />
            ))}
          </div>
        )}

        {!loading && topPosts.length === 0 && (
          <div className="px-3 py-6 text-center">
            <FireIcon className="h-8 w-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <Text size="xs" color="muted">
              As postagens com mais interações aparecerão aqui.
            </Text>
          </div>
        )}

        {!loading && topPosts.length > 0 && (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700/80">
            {topPosts.map((post, index) => {
              const rank = index + 1
              const reactions = post.reactions?.length ?? 0
              const comments = post.comments?.length ?? post.comment_count ?? 0
              const total = reactions + comments

              return (
                <li key={post.id}>
                  <button
                    type="button"
                    onClick={() => onSelectPost(post.id)}
                    className="w-full flex items-start gap-2.5 px-3 py-3 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <span
                      className={clsx(
                        'flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold',
                        rankBadgeClass(rank)
                      )}
                    >
                      {rank}
                    </span>

                    <AuthorAvatar
                      name={post.author?.name}
                      avatarPath={post.author?.avatar_url}
                      size="sm"
                    />

                    <span className="min-w-0 flex-1">
                      <Text size="xs" className="font-semibold text-gray-900 dark:text-white truncate">
                        {post.author?.name || 'Usuário'}
                      </Text>
                      <Text size="xs" color="muted" className="line-clamp-2 mt-0.5">
                        {mentionPreviewText(post.content, 70)}
                      </Text>
                      <span className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center gap-1">
                          <FaceSmileIcon className="h-3.5 w-3.5" />
                          {reactions}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <ChatBubbleOvalLeftIcon className="h-3.5 w-3.5" />
                          {comments}
                        </span>
                        <span className="font-medium text-primary-600 dark:text-primary-400">
                          {total} {total === 1 ? 'interação' : 'interações'}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </aside>
  )
}

export default TopPostsPanel
